import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./AdminLandingPage.module.css";
import AdminExpenseUnitsPage from "../AdminExpenseUnitsPage/AdminExpenseUnitsPage";
import { useUserContext } from "../../../contexts/UserContext";
import { useRoutesContext } from "../../../contexts/RoutesContext";
import { getSystemConfig, updateSystemConfig } from "../../../scripts/dataHandlers";

type AdminTab = "overview" | "expenseUnits" | "support";

const AdminLandingPage = () => {
  const { user } = useUserContext();
  const { routes } = useRoutesContext();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>("overview");
  const [maintenanceMode, setMaintenanceMode] = useState<boolean>(false);
  const [maintenanceModeLoading, setMaintenanceModeLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);

  // Fetch maintenance mode status on component mount
  useEffect(() => {
    const fetchMaintenanceMode = async () => {
      if (user?.isSuperAdmin) {
        try {
          setMaintenanceModeLoading(true);
          const response = await getSystemConfig("maintenanceMode");
          if (response?.data?.result?.value) {
            const value = response.data.result.value.toLowerCase();
            setMaintenanceMode(value === "true");
          } else {
            setMaintenanceMode(false);
          }
        } catch (error) {
          console.error("Failed to fetch maintenance mode:", error);
          setMaintenanceMode(false);
        } finally {
          setMaintenanceModeLoading(false);
        }
      }
    };

    fetchMaintenanceMode();
  }, [user]);

  // Redirect non-admin users away from admin page
  useEffect(() => {
    if (user && !user.isAdmin) {
      // User is not an admin - redirect to appropriate page
      if (user.isDoctor) {
        navigate(`/${routes.doctorMain}`);
      } else {
        navigate(`/${routes.main}`);
      }
    }
  }, [user, navigate, routes]);

  // Handle maintenance mode toggle
  const handleMaintenanceModeToggle = async () => {
    if (!user?.isSuperAdmin || updating) {
      return;
    }

    try {
      setUpdating(true);
      const newValue = !maintenanceMode;
      const response = await updateSystemConfig("maintenanceMode", newValue.toString());
      
      if (response?.data?.result?.success) {
        setMaintenanceMode(newValue);
      } else {
        console.error("Failed to update maintenance mode");
      }
    } catch (error) {
      console.error("Error updating maintenance mode:", error);
    } finally {
      setUpdating(false);
    }
  };

  // Don't render anything if user is not an admin (will be redirected)
  if (!user || !user.isAdmin) {
    return null;
  }

  // Determine which tabs to show based on admin roles
  // SuperAdmin sees all tabs by default
  const isSuperAdmin = user.isSuperAdmin === true;
  const isExpenseUnitAdmin = user.isExpenseUnitAdmin === true;
  const isSupportAdmin = user.isSupportAdmin === true;

  // Show tabs based on roles (SuperAdmin sees all)
  const showExpenseUnitsTab = isSuperAdmin || isExpenseUnitAdmin;
  const showSupportTab = isSuperAdmin || isSupportAdmin;

  return (
    <div className="mainContainer">
      <div className={classes.mainContainer}>
        <div className={classes.innerContainer}>
          <h1 className={classes.title}>Hallintapaneeli</h1>
          
          <div className={classes.tabRow}>
            <div
              className={
                activeTab === "overview"
                  ? classes.tabItemSelected
                  : classes.tabItem
              }
              onClick={() => setActiveTab("overview")}
            >
              Yleisnäkymä
            </div>
            {showExpenseUnitsTab && (
              <div
                className={
                  activeTab === "expenseUnits"
                    ? classes.tabItemSelected
                    : classes.tabItem
                }
                onClick={() => setActiveTab("expenseUnits")}
              >
                Kustannuspaikat
              </div>
            )}
            {showSupportTab && (
              <div
                className={
                  activeTab === "support"
                    ? classes.tabItemSelected
                    : classes.tabItem
                }
                onClick={() => setActiveTab("support")}
              >
                Tuki
              </div>
            )}
          </div>

          <div className={classes.tabContent}>
            {activeTab === "overview" && (
              <div className={classes.overviewContent}>
                <p className={classes.subtitle}>
                  Tervetuloa hallintapaneeliin.
                </p>
                {/* Maintenance mode toggle - only visible to super admins */}
                {isSuperAdmin && (
                  <div
                    style={{
                      marginTop: "30px",
                      padding: "20px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    <h3 style={{ marginTop: 0, marginBottom: "15px" }}>
                      Järjestelmän ylläpito
                    </h3>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "15px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          cursor: updating ? "not-allowed" : "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={maintenanceMode}
                          onChange={handleMaintenanceModeToggle}
                          disabled={maintenanceModeLoading || updating}
                          style={{
                            width: "20px",
                            height: "20px",
                            cursor: updating ? "not-allowed" : "pointer",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "16px",
                            fontWeight: maintenanceMode ? "bold" : "normal",
                          }}
                        >
                          Ylläpitotila:{" "}
                          <span
                            style={{
                              color: maintenanceMode ? "#d32f2f" : "#2e7d32",
                              fontWeight: "500",
                            }}
                          >
                            {maintenanceMode ? "Päällä" : "Pois päältä"}
                          </span>
                        </span>
                      </label>
                      {updating && (
                        <span style={{ color: "#666", fontSize: "14px" }}>
                          Päivitetään...
                        </span>
                      )}
                    </div>

                    <p
                      style={{
                        marginTop: "15px",
                        fontSize: "14px",
                        color: "#666",
                      }}
                    >
                      Kun ylläpitotila on päällä, vain pääylläpitäjät voivat
                      käyttää järjestelmää. Muut käyttäjät näkevät
                      ylläpitosivun.
                    </p>
                  </div>
                )}
              </div>
            )}
            {activeTab === "expenseUnits" && <AdminExpenseUnitsPage />}
            {activeTab === "support" && (
              <div className={classes.overviewContent}>
                <p className={classes.subtitle}>
                  Tuki-näkymä. Tämä osio on vielä kehityksessä.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLandingPage;
